import { FoulBuildUpRowData } from '../types/tender.types';
import { getPOSConfigurableField, makePOSRows, type POSRowDef } from './posRowDefs';

// Col indices (0-based): C=2, D=3, F=5
const DESC_COL = 2;
const QUANT_COL = 3;
const EVAL_COL = 5;

// Accept "PUBLIC OPEN SPACE <n>" for any n, in any column, with flexible
// spacing — sections are added/removed dynamically on the page now, so the
// sheet may contain more (or fewer) than the original 5.
// An optional trailing " - <text>" is the user's per-section title text (exported/typed as
// "PUBLIC OPEN SPACE 2 - North") and is read into `sectionTitles`.
const SECTION_TRIGGER_PATTERN = /^PUBLIC\s+OPEN\s+SPACE\s*(\d+)\s*(?:-\s*(.+?))?\s*$/i;

// Configurable rows (e.g. "xxxmm thick 'No Dig' layer...") carry a real mm
// value in the sheet where the app's own row def still has its "xx"/"xxx"
// placeholder — normalize both sides to the same token before comparing
// descriptions so those rows still match.
const normalizeDesc = (s: string): string =>
  s.trim().toLowerCase().replace(/(?:\d+(?:\.\d+)?|x+)\s*mm/gi, '#mm');

// Scan the first 8 columns of a row for a POS section trigger, returning the
// section number if found and not already triggered
const findSectionTrigger = (
  row: unknown[],
  triggeredSections: Set<number>,
): { n: number; title?: string } | null => {
  const scan = Math.min(row.length, 8);
  for (let c = 0; c < scan; c++) {
    const val = String(row[c] ?? '').trim();
    if (!val) continue;
    const match = val.match(SECTION_TRIGGER_PATTERN);
    if (match) {
      const n = Number(match[1]);
      if (!triggeredSections.has(n)) return { n, title: match[2]?.trim() || undefined };
    }
  }
  return null;
};

export interface POSImportResult {
  table: Record<string, FoulBuildUpRowData>;
  config: { sectionCount: number; rowThicknessMm: Record<string, number>; sectionTitles: Record<string, string> };
}

const parsePOSSheet = (rows: unknown[][]): POSImportResult => {
  const result: Record<string, FoulBuildUpRowData> = {};
  const rowThicknessMm: Record<string, number> = {};
  const sectionTitles: Record<string, string> = {};
  // The app's own ordered "data" rows for the current section — matching
  // against these (by description) is what determines which field a sheet
  // row's quantity belongs to, not the row's raw position in the sheet.
  let currentDefs: POSRowDef[] = [];
  let pointer = 0;
  const triggeredSections = new Set<number>();

  for (const row of rows) {
    // Check any column for a section trigger first
    const trigger = findSectionTrigger(row, triggeredSections);
    if (trigger !== null) {
      triggeredSections.add(trigger.n);
      if (trigger.title) sectionTitles[`pos_${trigger.n}_hdr`] = trigger.title;
      currentDefs = makePOSRows(trigger.n).filter((r) => r.rowType === 'data');
      pointer = 0;
      continue;
    }

    if (!currentDefs.length) continue;

    // Only read data from the designated description + quantity columns
    const desc = String(row[DESC_COL] ?? '').trim();
    if (!desc) continue;

    // Find which item this sheet row actually describes — searching forward
    // from the last match rather than trusting position — so a row the
    // sheet omits entirely (not just one left blank) doesn't shift every
    // later row in the section onto the wrong field id.
    const normalizedDesc = normalizeDesc(desc);
    let matchIndex = -1;
    for (let i = pointer; i < currentDefs.length; i++) {
      if (normalizeDesc(currentDefs[i].description) === normalizedDesc) {
        matchIndex = i;
        break;
      }
    }
    if (matchIndex === -1) continue; // no remaining item in this section describes this row

    const rowId = currentDefs[matchIndex].id;
    pointer = matchIndex + 1;

    // Rows whose static description carries an "xx"/"xxx" placeholder
    // thickness (e.g. "300mm thick 'No Dig' layer...") get their real mm
    // value read straight off the sheet's own description cell.
    const configurableField = getPOSConfigurableField(rowId);
    if (configurableField) {
      const mmMatch = desc.match(/(\d+(?:\.\d+)?)\s*mm/i);
      if (mmMatch) {
        const mm = Math.round(parseFloat(mmMatch[1]));
        if (mm > 0) rowThicknessMm[rowId] = mm;
      }
    }

    const rawQuant = row[QUANT_COL];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') continue;
    const qStr = String(rawQuant).trim();
    if (qStr.startsWith('#')) continue;
    const parsedQuant = parseFloat(qStr);
    if (isNaN(parsedQuant)) continue;
    // Round to the nearest whole number — imported quantities shouldn't carry
    // decimals through to the POS page.
    const q = Math.round(parsedQuant);

    const rawEval = String(row[EVAL_COL] ?? '').trim();
    const evaluateCode = rawEval.startsWith('#') ? '' : rawEval;

    result[rowId] = { quant: q, evaluateCode };
  }

  const sectionCount = triggeredSections.size > 0 ? Math.max(...triggeredSections) : 0;

  return { table: result, config: { sectionCount, rowThicknessMm, sectionTitles } };
};

export const parsePOSFromExcel = async (
  file: File,
): Promise<POSImportResult> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  const sheetName =
    workbook.SheetNames.find((n) => n.trim() === 'POS') ??
    workbook.SheetNames.find((n) => n.trim().toUpperCase() === 'POS');

  if (!sheetName) {
    throw new Error(
      `Sheet "POS" not found. Available sheets: ${workbook.SheetNames.join(', ')}`,
    );
  }

  const ws = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1,
    raw: true,
    defval: '',
  }) as unknown[][];

  console.group('📋 POS sheet import');
  console.log('Sheet name:', sheetName);
  console.log('Total rows:', rows.length);
  console.log('First 30 rows (raw):', rows.slice(0, 30));
  console.groupEnd();

  return parsePOSSheet(rows);
};
